import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Slider, Select, Typography, Tooltip, Spin, Input } from 'antd';
import apiClient from '../../../api/client';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * HalftoneEditorPanel — A CMYK halftone image filter panel for the Scrapbook.
 * Decomposes an image into CMYK channels and renders halftone dots at configurable
 * screen angles, with full control over frequency, dot size, noise/texture,
 * per-channel ink colors, and image pre-filtering.
 *
 * Props:
 *  - visible: boolean — controls panel visibility
 *  - onClose: () => void — cancel
 *  - onApply: (dataUrl: string) => void — apply halftoned image
 *  - sourceImageUrl: string — image to process
 *  - proxyUrl: string|null — optional proxy URL for CORS
 */
const HalftoneEditorPanel = ({ visible, onClose, onApply, sourceImageUrl, proxyUrl, canvasId }) => {
    const previewCanvasRef = useRef(null);
    const sourceImgRef = useRef(null);
    const processingRef = useRef(false);
    const pendingRef = useRef(false);
    const workerRef = useRef(null);
    const generationRef = useRef(0);

    // Halftone settings
    const [frequency, setFrequency] = useState(60);
    const [dotSize, setDotSize] = useState(1.0);
    const [roughness, setRoughness] = useState(2.0);
    const [fuzz, setFuzz] = useState(0.1);
    const [randomness, setRandomness] = useState(0.2);
    const [threshold, setThreshold] = useState(0.05);

    // Image pre-filtering
    const [contrast, setContrast] = useState(1.0);
    const [lightness, setLightness] = useState(0.0);
    const [blur, setBlur] = useState(0);

    // Noise & Texture
    const [paperNoise, setPaperNoise] = useState(0.0);
    const [inkNoise, setInkNoise] = useState(0.6);

    // Screen Angles (per channel, degrees)
    const [angleC, setAngleC] = useState(15);
    const [angleM, setAngleM] = useState(75);
    const [angleY, setAngleY] = useState(0);
    const [angleK, setAngleK] = useState(45);

    // Ink Colors (per channel: hex string, opacity 0-100, visible bool)
    const [inkC, setInkC] = useState({ hex: '00FFFF', opacity: 95, visible: true });
    const [inkM, setInkM] = useState({ hex: 'FF00FF', opacity: 95, visible: true });
    const [inkY, setInkY] = useState({ hex: 'FFFF00', opacity: 95, visible: true });
    const [inkK, setInkK] = useState({ hex: '000000', opacity: 95, visible: true });
    const [inkBg, setInkBg] = useState({ hex: 'F5F3F0', opacity: 100, visible: true });

    // User palettes
    const [userPalettes, setUserPalettes] = useState([]);
    useEffect(() => {
        if (!visible) return;
        const fetchPalettes = async () => {
            try {
                const response = await apiClient.get('/color-palettes', { params: canvasId ? { canvasId } : {} });
                setUserPalettes(response.data || []);
            } catch (e) { /* silent */ }
        };
        fetchPalettes();
    }, [visible, canvasId]);

    const applyInkPalette = (palette) => {
        const colors = (palette.colors || []).map(c => typeof c === 'string' ? c : c.color).filter(Boolean)
            .map(c => c.replace('#', ''));
        const setters = [setInkC, setInkM, setInkY, setInkK, setInkBg];
        colors.slice(0, 5).forEach((hex, i) => {
            setters[i](prev => ({ ...prev, hex }));
        });
    };

    // Blend mode
    const [blendMode, setBlendMode] = useState('cmyk');

    const [loading, setLoading] = useState(false);

    // Helper: parse hex to [r, g, b]
    const hexToRgb = (hex) => {
        const h = hex.replace('#', '');
        return [
            parseInt(h.substring(0, 2), 16) || 0,
            parseInt(h.substring(2, 4), 16) || 0,
            parseInt(h.substring(4, 6), 16) || 0,
        ];
    };

    // Load source image on open
    useEffect(() => {
        if (!visible || !sourceImageUrl) return;
        setLoading(true);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            sourceImgRef.current = img;
            setLoading(false);
        };
        img.onerror = () => {
            console.error('Failed to load source image for halftone');
            setLoading(false);
        };
        img.src = proxyUrl || sourceImageUrl;
    }, [visible, sourceImageUrl, proxyUrl]);

    // Effects worker lifecycle
    useEffect(() => {
        try {
            workerRef.current = new Worker(
                new URL('../workers/effectsWorker.js', import.meta.url)
            );
        } catch (err) {
            console.warn('Failed to create effects worker, falling back to main thread:', err);
        }
        return () => {
            if (workerRef.current) {
                workerRef.current.terminate();
                workerRef.current = null;
            }
        };
    }, []);

    // Trigger render when source loaded or any param changes
    useEffect(() => {
        if (!visible || !sourceImgRef.current) return;
        renderHalftone();
    }, [
        visible, frequency, dotSize, roughness, fuzz, randomness, threshold,
        contrast, lightness, blur,
        paperNoise, inkNoise,
        angleC, angleM, angleY, angleK,
        inkC, inkM, inkY, inkK, inkBg,
        blendMode,
    ]);

    // Also trigger initial render once image loads
    const prevLoadingRef = useRef(true);
    useEffect(() => {
        if (prevLoadingRef.current && !loading && sourceImgRef.current) {
            renderHalftone();
        }
        prevLoadingRef.current = loading;
    }, [loading]);

    // ========== Core CMYK Halftone Algorithm ==========

    const renderHalftone = useCallback(() => {
        if (!sourceImgRef.current || !previewCanvasRef.current) return;
        if (processingRef.current) { pendingRef.current = true; return; }
        processingRef.current = true;

        const worker = workerRef.current;
        const img = sourceImgRef.current;
        const maxDim = 800;
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);

        if (worker) {
            const generation = ++generationRef.current;
            // Source pixels (with blur if needed)
            const tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = w; tmpCanvas.height = h;
            const tmpCtx = tmpCanvas.getContext('2d');
            if (blur > 0) tmpCtx.filter = `blur(${blur}px)`;
            tmpCtx.drawImage(img, 0, 0, w, h);
            tmpCtx.filter = 'none';
            const srcBuf = tmpCtx.getImageData(0, 0, w, h).data.buffer.slice(0);
            // Alpha pixels (no blur)
            const alphaCanvas = document.createElement('canvas');
            alphaCanvas.width = w; alphaCanvas.height = h;
            alphaCanvas.getContext('2d').drawImage(img, 0, 0, w, h);
            const alphaBuf = alphaCanvas.getContext('2d').getImageData(0, 0, w, h).data.buffer.slice(0);

            worker.onmessage = (ev) => {
                if (ev.data.generation !== generationRef.current) return;
                if (ev.data.error === 'no-offscreen-canvas') {
                    // Fallback to main thread
                    try { doRender(); } finally {
                        processingRef.current = false;
                        if (pendingRef.current) { pendingRef.current = false; renderHalftone(); }
                    }
                    return;
                }
                const result = new Uint8ClampedArray(ev.data.resultPixels);
                const preview = previewCanvasRef.current;
                if (preview) {
                    preview.width = ev.data.width; preview.height = ev.data.height;
                    preview.getContext('2d').putImageData(new ImageData(result, ev.data.width, ev.data.height), 0, 0);
                }
                processingRef.current = false;
                if (pendingRef.current) { pendingRef.current = false; renderHalftone(); }
            };

            worker.postMessage({
                type: 'halftone', srcPixels: srcBuf, alphaPixels: alphaBuf,
                width: w, height: h,
                frequency, dotSize, roughness, fuzz, randomness, threshold,
                contrast, lightness, paperNoise, inkNoise,
                angleC, angleM, angleY, angleK,
                inkC, inkM, inkY, inkK, inkBg,
                blendMode, generation,
            }, [srcBuf, alphaBuf]);
        } else {
            requestAnimationFrame(() => {
                try { doRender(); } finally {
                    processingRef.current = false;
                    if (pendingRef.current) { pendingRef.current = false; renderHalftone(); }
                }
            });
        }
    }, [
        frequency, dotSize, roughness, fuzz, randomness, threshold,
        contrast, lightness, blur,
        paperNoise, inkNoise,
        angleC, angleM, angleY, angleK,
        inkC, inkM, inkY, inkK, inkBg,
        blendMode,
    ]);

    const doRender = () => {
        const img = sourceImgRef.current;
        const canvas = previewCanvasRef.current;
        if (!img || !canvas) return;

        const maxDim = 800;
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);

        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');

        // Step 1: Pre-filter
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = w;
        tempCanvas.height = h;
        const tempCtx = tempCanvas.getContext('2d');
        if (blur > 0) tempCtx.filter = `blur(${blur}px)`;
        tempCtx.drawImage(img, 0, 0, w, h);
        tempCtx.filter = 'none';

        const imageData = tempCtx.getImageData(0, 0, w, h);
        const pixels = imageData.data;

        for (let i = 0; i < pixels.length; i += 4) {
            for (let c = 0; c < 3; c++) {
                let val = pixels[i + c] / 255;
                val += lightness;
                val = (val - 0.5) * contrast + 0.5;
                pixels[i + c] = Math.max(0, Math.min(255, Math.round(val * 255)));
            }
        }

        // Step 2: Fill background
        const bgRgb = hexToRgb(inkBg.hex);
        const bgAlpha = inkBg.opacity / 100;
        if (inkBg.visible) {
            ctx.fillStyle = `rgba(${bgRgb[0]}, ${bgRgb[1]}, ${bgRgb[2]}, ${bgAlpha})`;
            ctx.fillRect(0, 0, w, h);
        } else {
            ctx.clearRect(0, 0, w, h);
        }

        // Step 3: Apply paper noise
        if (paperNoise > 0 && inkBg.visible) {
            const noiseData = ctx.getImageData(0, 0, w, h);
            const nd = noiseData.data;
            for (let i = 0; i < nd.length; i += 4) {
                const noise = (Math.random() - 0.5) * 255 * paperNoise * 0.3;
                nd[i] = Math.max(0, Math.min(255, nd[i] + noise));
                nd[i + 1] = Math.max(0, Math.min(255, nd[i + 1] + noise));
                nd[i + 2] = Math.max(0, Math.min(255, nd[i + 2] + noise));
            }
            ctx.putImageData(noiseData, 0, 0);
        }

        // Step 4: Render halftone channels
        const gridSpacing = Math.max(2, Math.round(300 / frequency));

        if (blendMode === 'mono') {
            if (inkK.visible) {
                const kRgb = hexToRgb(inkK.hex);
                renderChannel(ctx, pixels, w, h, gridSpacing, angleK, kRgb, inkK.opacity / 100, 'mono');
            }
        } else if (blendMode === 'duotone') {
            if (inkK.visible) {
                const kRgb = hexToRgb(inkK.hex);
                renderChannel(ctx, pixels, w, h, gridSpacing, angleK, kRgb, inkK.opacity / 100, 'mono');
            }
            if (inkC.visible) {
                const cRgb = hexToRgb(inkC.hex);
                renderChannel(ctx, pixels, w, h, gridSpacing, angleC, cRgb, inkC.opacity / 100, 'cyan-only');
            }
        } else {
            // CMYK — render Y, M, C, K in order (lightest first)
            const channels = [
                { angleDeg: angleY, ink: inkY, key: 'y' },
                { angleDeg: angleM, ink: inkM, key: 'm' },
                { angleDeg: angleC, ink: inkC, key: 'c' },
                { angleDeg: angleK, ink: inkK, key: 'k' },
            ];
            for (const ch of channels) {
                if (!ch.ink.visible) continue;
                const rgb = hexToRgb(ch.ink.hex);
                renderChannel(ctx, pixels, w, h, gridSpacing, ch.angleDeg, rgb, ch.ink.opacity / 100, ch.key);
            }
        }

        // Step 5: Alpha mask — clip to original image's transparency
        // Draw the original image onto a temp canvas to extract its alpha,
        // then use destination-in to mask the halftone result
        const alphaCanvas = document.createElement('canvas');
        alphaCanvas.width = w;
        alphaCanvas.height = h;
        const alphaCtx = alphaCanvas.getContext('2d');
        alphaCtx.drawImage(img, 0, 0, w, h);
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(alphaCanvas, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
    };

    const renderChannel = (ctx, pixels, w, h, gridSpacing, angleDeg, color, opacity, channelKey) => {
        const angle = (angleDeg * Math.PI) / 180;
        const cosA = Math.cos(angle);
        const sinA = Math.sin(angle);
        const maxRadius = (gridSpacing * dotSize) / 2;

        const diagonal = Math.sqrt(w * w + h * h);
        const gridCountX = Math.ceil(diagonal / gridSpacing) + 4;
        const gridCountY = Math.ceil(diagonal / gridSpacing) + 4;
        const offsetX = w / 2;
        const offsetY = h / 2;

        ctx.save();
        ctx.globalCompositeOperation = 'multiply';
        ctx.globalAlpha = opacity;

        const seedOffset = channelKey === 'c' ? 0 : channelKey === 'm' ? 1000 : channelKey === 'y' ? 2000 : 3000;

        for (let gy = -gridCountY / 2; gy < gridCountY / 2; gy++) {
            for (let gx = -gridCountX / 2; gx < gridCountX / 2; gx++) {
                const localX = gx * gridSpacing;
                const localY = gy * gridSpacing;

                let px = localX * cosA - localY * sinA + offsetX;
                let py = localX * sinA + localY * cosA + offsetY;

                if (randomness > 0) {
                    const hash = pseudoRandom(gx + seedOffset, gy + seedOffset);
                    const hash2 = pseudoRandom(gy + seedOffset, gx + seedOffset + 500);
                    px += (hash - 0.5) * gridSpacing * randomness;
                    py += (hash2 - 0.5) * gridSpacing * randomness;
                }

                if (px < -maxRadius || px > w + maxRadius || py < -maxRadius || py > h + maxRadius) continue;

                const sx = Math.round(Math.max(0, Math.min(w - 1, px)));
                const sy = Math.round(Math.max(0, Math.min(h - 1, py)));
                const idx = (sy * w + sx) * 4;

                const r = pixels[idx] / 255;
                const g = pixels[idx + 1] / 255;
                const b = pixels[idx + 2] / 255;

                let intensity;
                if (channelKey === 'mono') {
                    intensity = 1 - (0.299 * r + 0.587 * g + 0.114 * b);
                } else if (channelKey === 'cyan-only') {
                    intensity = 1 - r;
                } else {
                    const k = 1 - Math.max(r, g, b);
                    if (k >= 1) {
                        intensity = channelKey === 'k' ? 1 : 0;
                    } else {
                        const c = (1 - r - k) / (1 - k);
                        const m = (1 - g - k) / (1 - k);
                        const y = (1 - b - k) / (1 - k);
                        switch (channelKey) {
                            case 'c': intensity = c; break;
                            case 'm': intensity = m; break;
                            case 'y': intensity = y; break;
                            case 'k': intensity = k; break;
                            default: intensity = 0;
                        }
                    }
                }

                if (intensity < threshold) continue;

                intensity = Math.pow(intensity, 1 - fuzz * 0.5);

                let radius = maxRadius * Math.sqrt(intensity);

                if (roughness > 0) {
                    const rHash = pseudoRandom(gx * 7 + seedOffset, gy * 13 + seedOffset);
                    radius *= 1 + (rHash - 0.5) * roughness * 0.15;
                }

                // Ink noise — jitter per-dot opacity
                let dotAlpha = 1;
                if (inkNoise > 0) {
                    const nHash = pseudoRandom(gx * 3 + seedOffset + 777, gy * 11 + seedOffset + 333);
                    dotAlpha = 1 - (1 - nHash) * inkNoise * 0.5;
                }

                if (radius < 0.3) continue;

                ctx.fillStyle = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${dotAlpha})`;
                ctx.beginPath();
                ctx.arc(px, py, radius, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        ctx.restore();
    };

    const pseudoRandom = (x, y) => {
        let n = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
        return n - Math.floor(n);
    };

    // ========== Handle Apply ==========
    const handleApply = useCallback(() => {
        const img = sourceImgRef.current;
        if (!img) return;
        const worker = workerRef.current;
        const w = img.width, h = img.height;
        const previewW = previewCanvasRef.current?.width || 800;
        const resScale = w / previewW;

        if (worker) {
            const generation = ++generationRef.current;
            const tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = w; tmpCanvas.height = h;
            const tmpCtx = tmpCanvas.getContext('2d');
            if (blur > 0) tmpCtx.filter = `blur(${blur * resScale}px)`;
            tmpCtx.drawImage(img, 0, 0, w, h);
            tmpCtx.filter = 'none';
            const srcBuf = tmpCtx.getImageData(0, 0, w, h).data.buffer.slice(0);
            const alphaCanvas = document.createElement('canvas');
            alphaCanvas.width = w; alphaCanvas.height = h;
            alphaCanvas.getContext('2d').drawImage(img, 0, 0, w, h);
            const alphaBuf = alphaCanvas.getContext('2d').getImageData(0, 0, w, h).data.buffer.slice(0);

            const prevOnMsg = worker.onmessage;
            worker.onmessage = (ev) => {
                if (ev.data.generation !== generationRef.current) return;
                if (ev.data.error === 'no-offscreen-canvas') {
                    // Fallback to main-thread apply
                    worker.onmessage = prevOnMsg;
                    handleApplyMainThread();
                    return;
                }
                const result = new Uint8ClampedArray(ev.data.resultPixels);
                const outCanvas = document.createElement('canvas');
                outCanvas.width = ev.data.width; outCanvas.height = ev.data.height;
                outCanvas.getContext('2d').putImageData(new ImageData(result, ev.data.width, ev.data.height), 0, 0);
                onApply?.(outCanvas.toDataURL('image/png'));
            };

            worker.postMessage({
                type: 'halftone', srcPixels: srcBuf, alphaPixels: alphaBuf,
                width: w, height: h,
                frequency, dotSize, roughness, fuzz, randomness, threshold,
                contrast, lightness, paperNoise, inkNoise,
                angleC, angleM, angleY, angleK,
                inkC, inkM, inkY, inkK, inkBg,
                blendMode, generation,
            }, [srcBuf, alphaBuf]);
        } else {
            handleApplyMainThread();
        }
    }, [
        frequency, dotSize, roughness, fuzz, randomness, threshold,
        contrast, lightness, blur,
        paperNoise, inkNoise,
        angleC, angleM, angleY, angleK,
        inkC, inkM, inkY, inkK, inkBg,
        blendMode, onApply,
    ]);

    // Main-thread fallback for handleApply
    const handleApplyMainThread = () => {
        const canvas = previewCanvasRef.current;
        if (!canvas) return;
        const img = sourceImgRef.current;
        if (!img) return;

        const fullCanvas = document.createElement('canvas');
        fullCanvas.width = img.width;
        fullCanvas.height = img.height;
        const fullCtx = fullCanvas.getContext('2d');

        const w = img.width;
        const h = img.height;
        const previewW = previewCanvasRef.current.width || 800;
        const resScale = w / previewW;

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = w; tempCanvas.height = h;
        const tempCtx = tempCanvas.getContext('2d');
        if (blur > 0) tempCtx.filter = `blur(${blur * resScale}px)`;
        tempCtx.drawImage(img, 0, 0, w, h);
        tempCtx.filter = 'none';

        const imageData = tempCtx.getImageData(0, 0, w, h);
        const pixels = imageData.data;
        for (let i = 0; i < pixels.length; i += 4) {
            for (let c = 0; c < 3; c++) {
                let val = pixels[i + c] / 255 + lightness;
                val = (val - 0.5) * contrast + 0.5;
                pixels[i + c] = Math.max(0, Math.min(255, Math.round(val * 255)));
            }
        }

        const bgRgb = hexToRgb(inkBg.hex);
        if (inkBg.visible) {
            fullCtx.fillStyle = `rgba(${bgRgb[0]}, ${bgRgb[1]}, ${bgRgb[2]}, ${inkBg.opacity / 100})`;
            fullCtx.fillRect(0, 0, w, h);
        }

        if (paperNoise > 0 && inkBg.visible) {
            const noiseData = fullCtx.getImageData(0, 0, w, h);
            const nd = noiseData.data;
            for (let i = 0; i < nd.length; i += 4) {
                const noise = (Math.random() - 0.5) * 255 * paperNoise * 0.3;
                nd[i] = Math.max(0, Math.min(255, nd[i] + noise));
                nd[i + 1] = Math.max(0, Math.min(255, nd[i + 1] + noise));
                nd[i + 2] = Math.max(0, Math.min(255, nd[i + 2] + noise));
            }
            fullCtx.putImageData(noiseData, 0, 0);
        }

        const fullGridSpacing = Math.max(2, Math.round(300 / frequency)) * resScale;
        if (blendMode === 'mono') {
            if (inkK.visible) renderChannel(fullCtx, pixels, w, h, fullGridSpacing, angleK, hexToRgb(inkK.hex), inkK.opacity / 100, 'mono');
        } else if (blendMode === 'duotone') {
            if (inkK.visible) renderChannel(fullCtx, pixels, w, h, fullGridSpacing, angleK, hexToRgb(inkK.hex), inkK.opacity / 100, 'mono');
            if (inkC.visible) renderChannel(fullCtx, pixels, w, h, fullGridSpacing, angleC, hexToRgb(inkC.hex), inkC.opacity / 100, 'cyan-only');
        } else {
            for (const ch of [
                { angleDeg: angleY, ink: inkY, key: 'y' }, { angleDeg: angleM, ink: inkM, key: 'm' },
                { angleDeg: angleC, ink: inkC, key: 'c' }, { angleDeg: angleK, ink: inkK, key: 'k' },
            ]) { if (ch.ink.visible) renderChannel(fullCtx, pixels, w, h, fullGridSpacing, ch.angleDeg, hexToRgb(ch.ink.hex), ch.ink.opacity / 100, ch.key); }
        }

        const alphaCanvas2 = document.createElement('canvas');
        alphaCanvas2.width = w; alphaCanvas2.height = h;
        alphaCanvas2.getContext('2d').drawImage(img, 0, 0, w, h);
        fullCtx.globalCompositeOperation = 'destination-in';
        fullCtx.drawImage(alphaCanvas2, 0, 0);
        fullCtx.globalCompositeOperation = 'source-over';
        onApply?.(fullCanvas.toDataURL('image/png'));
    };

    // ========== UI Helpers ==========
    const sliderRow = (label, min, max, value, onChange, step = 1) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80, whiteSpace: 'nowrap' }}>{label}</Text>
            <Slider
                min={min} max={max} step={step} value={value} onChange={onChange}
                style={{ flex: 1, margin: 0 }}
                styles={{
                    track: { background: '#a78bfa' },
                    rail: { background: '#30363d' },
                    handle: { borderColor: '#a78bfa', width: 12, height: 12 }
                }}
            />
            <Text style={{ color: '#a78bfa', fontSize: 11, fontFamily: 'monospace', minWidth: 36, textAlign: 'right' }}>
                {step < 1 ? value.toFixed(2) : value}
            </Text>
        </div>
    );

    // Ink color row with swatch, hex input, opacity, visibility
    const inkColorRow = (label, inkState, setInkState) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 70, whiteSpace: 'nowrap' }}>{label}</Text>
            {/* Color swatch */}
            <div style={{ position: 'relative' }}>
                <div
                    style={{
                        width: 24,
                        height: 24,
                        borderRadius: 4,
                        background: `#${inkState.hex}`,
                        border: '1px solid #30363d',
                        cursor: 'pointer',
                    }}
                    onClick={() => {
                        // Open native color picker via hidden input
                        const input = document.createElement('input');
                        input.type = 'color';
                        input.value = `#${inkState.hex}`;
                        input.style.position = 'absolute';
                        input.style.opacity = '0';
                        input.style.pointerEvents = 'none';
                        document.body.appendChild(input);
                        input.addEventListener('input', (e) => {
                            setInkState(prev => ({ ...prev, hex: e.target.value.replace('#', '').toUpperCase() }));
                        });
                        input.addEventListener('change', () => {
                            document.body.removeChild(input);
                        });
                        input.click();
                    }}
                />
            </div>
            {/* Hex input */}
            <Input
                value={inkState.hex}
                onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9A-Fa-f]/g, '').substring(0, 6).toUpperCase();
                    setInkState(prev => ({ ...prev, hex: val }));
                }}
                style={{
                    width: 72,
                    background: '#21262d',
                    borderColor: '#30363d',
                    color: '#c9d1d9',
                    fontSize: 11,
                    fontFamily: 'monospace',
                    textAlign: 'center',
                    padding: '2px 4px',
                    height: 26,
                }}
                size="small"
            />
            {/* Opacity input */}
            <Input
                value={inkState.opacity}
                onChange={(e) => {
                    let val = parseInt(e.target.value) || 0;
                    val = Math.max(0, Math.min(100, val));
                    setInkState(prev => ({ ...prev, opacity: val }));
                }}
                suffix="%"
                style={{
                    width: 56,
                    background: '#21262d',
                    borderColor: '#30363d',
                    color: '#c9d1d9',
                    fontSize: 11,
                    fontFamily: 'monospace',
                    textAlign: 'center',
                    padding: '2px 4px',
                    height: 26,
                }}
                size="small"
            />
            {/* Visibility toggle */}
            <Tooltip title={inkState.visible ? 'Hide channel' : 'Show channel'}>
                <Button
                    type="text"
                    size="small"
                    icon={inkState.visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    onClick={() => setInkState(prev => ({ ...prev, visible: !prev.visible }))}
                    style={{ color: inkState.visible ? '#8b949e' : '#484f58', padding: '0 4px', minWidth: 24 }}
                />
            </Tooltip>
        </div>
    );

    // Screen angle row with slider 0-360
    const angleRow = (label, colorHex, value, onChange) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: `#${colorHex}`, border: '1px solid #30363d', flexShrink: 0 }} />
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 56, whiteSpace: 'nowrap' }}>{label}</Text>
            <Slider
                min={0} max={180} step={1} value={value} onChange={onChange}
                style={{ flex: 1, margin: 0 }}
                styles={{
                    track: { background: '#a78bfa' },
                    rail: { background: '#30363d' },
                    handle: { borderColor: '#a78bfa', width: 12, height: 12 }
                }}
            />
            <Text style={{ color: '#a78bfa', fontSize: 11, fontFamily: 'monospace', minWidth: 28, textAlign: 'right' }}>
                {value}°
            </Text>
        </div>
    );

    if (!visible) return null;

    return (
        <div
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 110,
                display: 'flex',
                background: 'rgba(13, 17, 23, 0.95)',
                pointerEvents: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Left — Preview Canvas */}
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 24,
                    overflow: 'hidden',
                    position: 'relative',
                }}
            >
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <Spin size="large" />
                        <Text style={{ color: '#8b949e' }}>Loading image...</Text>
                    </div>
                ) : (
                    <canvas
                        ref={previewCanvasRef}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            borderRadius: 8,
                            boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
                            border: '1px solid #30363d',
                        }}
                    />
                )}
            </div>

            {/* Right — Controls Panel */}
            <div style={{
                width: 310,
                flexShrink: 0,
                borderLeft: '1px solid #30363d',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                background: '#0d1117',
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    borderBottom: '1px solid #30363d',
                    background: '#161b22',
                    flexShrink: 0,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16 }}>◉</span>
                        <span style={{ color: '#c9d1d9', fontWeight: 600, fontSize: 14 }}>Halftone</span>
                    </div>
                    <Button
                        type="text"
                        size="small"
                        onClick={onClose}
                        style={{ color: '#8b949e', fontSize: 12, padding: '2px 6px' }}
                    >
                        ✕
                    </Button>
                </div>

                {/* Scrollable Controls */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 16 }}>

                    {/* Blend Mode */}
                    <div>
                        <Text style={{ color: '#6e7681', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>
                            Blend Mode
                        </Text>
                        <Select
                            value={blendMode}
                            onChange={setBlendMode}
                            style={{ width: '100%', marginTop: 6 }}
                            dropdownStyle={{ background: '#21262d', border: '1px solid #30363d' }}
                            options={[
                                { value: 'cmyk', label: 'Subtractive (CMYK)' },
                                { value: 'mono', label: 'Monochrome' },
                                { value: 'duotone', label: 'Duotone' },
                            ]}
                        />
                    </div>

                    {/* Halftone Settings */}
                    <div>
                        <Text style={{ color: '#a78bfa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Halftone Settings
                        </Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Frequency', 10, 300, frequency, setFrequency)}
                            {sliderRow('Dot Size', 0.1, 3.0, dotSize, (v) => setDotSize(v), 0.01)}
                            {sliderRow('Roughness', 0, 10, roughness, (v) => setRoughness(v), 0.1)}
                            {sliderRow('Fuzz', 0, 1, fuzz, (v) => setFuzz(v), 0.01)}
                            {sliderRow('Randomness', 0, 1, randomness, (v) => setRandomness(v), 0.01)}
                            {sliderRow('Threshold', 0, 0.5, threshold, (v) => setThreshold(v), 0.01)}
                        </div>
                    </div>

                    {/* Screen Angles */}
                    <div>
                        <Text style={{ color: '#a78bfa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Screen Angles
                        </Text>
                        <div style={{ marginTop: 8 }}>
                            {angleRow('Cyan', inkC.hex, angleC, setAngleC)}
                            {angleRow('Magenta', inkM.hex, angleM, setAngleM)}
                            {angleRow('Yellow', inkY.hex, angleY, setAngleY)}
                            {angleRow('Black', '666666', angleK, setAngleK)}
                        </div>
                    </div>

                    {/* Image Pre-filtering */}
                    <div>
                        <Text style={{ color: '#a78bfa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Image (Pre-filtering)
                        </Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Contrast', 0.5, 2.0, contrast, (v) => setContrast(v), 0.01)}
                            {sliderRow('Lightness', -0.5, 0.5, lightness, (v) => setLightness(v), 0.01)}
                            {sliderRow('Blur', 0, 5, blur, (v) => setBlur(v), 0.1)}
                        </div>
                    </div>

                    {/* Noise & Texture */}
                    <div>
                        <Text style={{ color: '#a78bfa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Noise & Texture
                        </Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Paper Noise', 0, 1, paperNoise, (v) => setPaperNoise(v), 0.01)}
                            {sliderRow('Ink Noise', 0, 1, inkNoise, (v) => setInkNoise(v), 0.01)}
                        </div>
                    </div>

                    {/* Ink Colors */}
                    <div>
                        <Text style={{ color: '#a78bfa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Ink Colors
                        </Text>
                        <div style={{ marginTop: 8 }}>
                            {inkColorRow('Cyan', inkC, setInkC)}
                            {inkColorRow('Magenta', inkM, setInkM)}
                            {inkColorRow('Yellow', inkY, setInkY)}
                            {inkColorRow('Black', inkK, setInkK)}
                            <div style={{ height: 1, background: '#21262d', margin: '6px 0' }} />
                            {inkColorRow('Background', inkBg, setInkBg)}
                        </div>

                        {/* User Palettes */}
                        {userPalettes.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                <Text style={{ color: '#6e7681', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>From Palette</Text>
                                <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {userPalettes.map((palette) => {
                                        const colors = (palette.colors || []).map(c => typeof c === 'string' ? c : c.color).filter(Boolean);
                                        if (colors.length === 0) return null;
                                        return (
                                            <div
                                                key={palette.id || palette.name}
                                                onClick={() => applyInkPalette(palette)}
                                                style={{
                                                    cursor: 'pointer', padding: '4px 6px', borderRadius: 4,
                                                    border: '1px solid #30363d', background: '#161b22',
                                                    transition: 'all 0.15s ease',
                                                }}
                                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#a78bfa'; e.currentTarget.style.background = '#1c2333'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#30363d'; e.currentTarget.style.background = '#161b22'; }}
                                            >
                                                <Text style={{ color: '#c9d1d9', fontSize: 10, display: 'block', marginBottom: 3 }}>{palette.name}</Text>
                                                <div style={{ display: 'flex', gap: 2 }}>
                                                    {colors.map((c, i) => (
                                                        <div key={i} style={{ width: 14, height: 14, borderRadius: 2, background: c, border: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }} />
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div style={{
                    padding: '12px 14px',
                    borderTop: '1px solid #30363d',
                    display: 'flex',
                    gap: 8,
                    flexShrink: 0,
                    background: '#161b22',
                }}>
                    <Button
                        onClick={onClose}
                        style={{
                            flex: 1,
                            background: '#21262d',
                            borderColor: '#30363d',
                            color: '#c9d1d9',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleApply}
                        style={{
                            flex: 1,
                            background: '#a78bfa',
                            borderColor: '#a78bfa',
                        }}
                    >
                        Apply
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default HalftoneEditorPanel;
